import { LocalStorageKey } from "@/utils/local-storage-utils";

export interface UTMParameters {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

/**
 * Extract UTM parameters from URL search params
 */
export const extractUTMParameters = (searchParams: URLSearchParams): UTMParameters => {
  const utmParams: UTMParameters = {};

  const utmKeys: (keyof UTMParameters)[] = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ];

  for (const key of utmKeys) {
    const value = searchParams.get(key);
    if (value) {
      utmParams[key] = value;
    }
  }

  return utmParams;
};

/**
 * Get UTM parameters from the current URL
 */
export const getUTMParametersFromURL = (): UTMParameters => {
  if (typeof window === "undefined") {
    return {};
  }

  const searchParams = new URLSearchParams(window.location.search);
  return extractUTMParameters(searchParams);
};

/**
 * Store UTM parameters in sessionStorage (current session)
 */
export const storeSessionUTMParameters = (params: UTMParameters): void => {
  if (typeof window === "undefined" || Object.keys(params).length === 0) {
    return;
  }

  try {
    sessionStorage.setItem(LocalStorageKey.UTM_PARAMS, JSON.stringify(params));
  } catch (err) {
    console.warn("Failed to store UTM parameters in sessionStorage:", err);
  }
};

/**
 * Store initial UTM parameters in localStorage (first visit only)
 */
export const storeInitialUTMParameters = (params: UTMParameters): void => {
  if (typeof window === "undefined" || Object.keys(params).length === 0) {
    return;
  }

  try {
    // Only store if there are no initial UTM parameters already stored
    const existing = localStorage.getItem(LocalStorageKey.INITIAL_UTM_PARAMS);
    if (existing == null) {
      localStorage.setItem(LocalStorageKey.INITIAL_UTM_PARAMS, JSON.stringify(params));
    }
  } catch (err) {
    console.warn("Failed to store initial UTM parameters in localStorage:", err);
  }
};

/**
 * Get UTM parameters from sessionStorage
 */
export const getSessionUTMParameters = (): UTMParameters => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const stored = sessionStorage.getItem(LocalStorageKey.UTM_PARAMS);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (err) {
    console.warn("Failed to retrieve UTM parameters from sessionStorage:", err);
  }

  return {};
};

/**
 * Get initial UTM parameters from localStorage
 */
export const getInitialUTMParameters = (): UTMParameters => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const stored = localStorage.getItem(LocalStorageKey.INITIAL_UTM_PARAMS);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (err) {
    console.warn("Failed to retrieve initial UTM parameters from localStorage:", err);
  }

  return {};
};

/**
 * Capture and store UTM parameters from the current URL
 * Should be called on page load
 */
export const captureAndStoreUTMParameters = (): void => {
  const params = getUTMParametersFromURL();

  if (Object.keys(params).length > 0) {
    storeSessionUTMParameters(params);
    storeInitialUTMParameters(params);
  }
};

/**
 * Get all UTM parameters (both session and initial) for form submission
 * Returns an object with prefixed keys for clarity
 */
export const getUTMParametersForSubmission = (): Record<string, string> => {
  const sessionParams = getSessionUTMParameters();
  const initialParams = getInitialUTMParameters();

  const result: Record<string, string> = {};

  // Add session UTM parameters
  for (const [key, value] of Object.entries(sessionParams)) {
    if (value) {
      result[key] = value;
    }
  }

  // Add initial UTM parameters with prefix
  for (const [key, value] of Object.entries(initialParams)) {
    if (value) {
      result[`initial_${key}`] = value;
    }
  }

  return result;
};
